import React, { useState } from "react";
import { CodeBlock } from "./CodeBlock";
import { formatJSON } from "../lib/json_utils";

interface JSONViewerProps {
  data: Record<string, any> | null | undefined;
  title?: string;
  className?: string;
  maxHeight?: string;
}

/**
 * A component for displaying JSON data with a copy-to-clipboard button.
 * Always shows formatted JSON.
 */
export function JSONViewer({
  data,
  title,
  className = "",
  maxHeight = "max-h-96",
}: JSONViewerProps) {
  const [copied, setCopied] = useState(false);

  if (!data) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>No data available</div>
    );
  }

  const jsonString = formatJSON(data);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  return (
    <div className={className}>
      {title && (
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
        </div>
      )}
      <div className="mb-3 flex justify-end">
        <button
          onClick={handleCopy}
          className="p-2 rounded hover:bg-accent transition-colors"
          title={copied ? "Copied!" : "Copy to clipboard"}
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-success"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          )}
        </button>
      </div>
      <CodeBlock
        content={jsonString}
        variant="formatted"
        maxHeight={maxHeight}
      />
    </div>
  );
}
